/**
 * FRICTION — core data model. This file is the CONTRACT between map generation, simulation,
 * AI, networking and rendering. Change it deliberately and update docs/Engine.md.
 *
 * Conventions
 *  - One tick = one in-game hour. Tick 0 = Day 1, 06:00.
 *  - All per-hex data lives in typed arrays indexed by HexId (see hex.ts).
 *  - Fractions (strength, org, supply …) are floats in [0, 1].
 *  - No classes in state: everything must be structured-clone / msgpack friendly.
 */
import type { HexId } from './hex';

export type Tick = number;
export type FactionId = number;
export type UnitId = number;
export type CityId = number;
export type OrderId = number;

/**
 * Owner value for water / unclaimed hexes. Land that starts unclaimed (`GameMap.initialOwner`) is
 * a neutral country of a real-world theatre: closed to every unit, courier and supply line.
 */
export const NO_FACTION = 255;
export const MAX_FACTIONS = 6;

// ---------------------------------------------------------------------------------------------
// Static map (generated once from a seed, sent to clients at game start)
// ---------------------------------------------------------------------------------------------

export const Terrain = {
  Ocean: 0,
  Shallows: 1,
  Lake: 2,
  Plains: 3,
  Forest: 4,
  Hills: 5,
  Mountains: 6,
  Marsh: 7,
  Urban: 8,
} as const;
export type Terrain = (typeof Terrain)[keyof typeof Terrain];
export const TERRAIN_COUNT = 9;

export const isWater = (t: number): boolean =>
  t === Terrain.Ocean || t === Terrain.Shallows || t === Terrain.Lake;

export interface City {
  id: CityId;
  hex: HexId;
  name: string;
  /** 1 = town, 2 = city, 3 = metropolis. */
  size: 1 | 2 | 3;
  /** Faction whose starting capital this is, or NO_FACTION. */
  capitalOf: FactionId;
  /** Victory points for holding it. */
  vp: number;
  /** Industry points produced per day while controlled and in supply. */
  industry: number;
  /** Manpower (thousands of men) produced per day while controlled. */
  manpower: number;
  /** Adjacent to sea/lake water. */
  coastal: boolean;
}

/** A river rendered as a chain of hex-corner points; gameplay uses `GameMap.riverEdges`. */
export interface RiverPath {
  /** World-space polyline [x0, y0, x1, y1, …] following hex edges from source to mouth. */
  points: number[];
  /** Width class that grows downstream per vertex, same length as points.length / 2. 1..4 */
  widths: number[];
}

export interface GameMap {
  seed: number;
  /** Fictional theatre name, e.g. "The Varnian Marches". */
  name: string;
  width: number;
  height: number;
  /** Terrain per hex. */
  terrain: Uint8Array;
  /** Elevation per hex 0..255 (sea level ≈ 64). Purely continuous-looking data for rendering + sight. */
  elevation: Uint8Array;
  /** Moisture per hex 0..255 (drives vegetation tint in the renderer). */
  moisture: Uint8Array;
  /** Bitmask per hex: bit d set ⇔ a river runs along the edge facing direction d (symmetric). */
  riverEdges: Uint8Array;
  rivers: RiverPath[];
  /** Bitmask per hex: bit d set ⇔ a road connects this hex with its neighbour in direction d (symmetric). */
  roads: Uint8Array;
  /** Bitmask per hex for railways (same encoding as roads). Railways speed supply, not units. */
  rails: Uint8Array;
  cities: City[];
  /** City id per hex, or -1. */
  cityAt: Int16Array;
  /** Starting owner per hex (NO_FACTION for water / neutral). */
  initialOwner: Uint8Array;
  /** Region (named province) id per hex, -1 for water. Regions are the naming/label layer. */
  region: Int16Array;
  regions: { id: number; name: string /** label anchor hex */; hex: HexId }[];
  factionCount: number;
  /**
   * Kilometres between neighbouring hex centres, for real-world theatres (theatres/index.ts);
   * absent on generated maps (the renderer's scale bar then assumes 10 km).
   */
  hexKm?: number;
}

// ---------------------------------------------------------------------------------------------
// Units, orders, couriers, battles
// ---------------------------------------------------------------------------------------------

export type UnitType = 'infantry' | 'motorized' | 'armor' | 'artillery' | 'recon' | 'hq' | 'decoy';
export const UNIT_TYPES: readonly UnitType[] = [
  'infantry',
  'motorized',
  'armor',
  'artillery',
  'recon',
  'hq',
  'decoy',
];

/** Standing rules of engagement when the unit has no order that says otherwise. */
export type Posture = 'aggressive' | 'balanced' | 'defensive';

/**
 * move    – march to target; stops short of enemy units, never initiates combat.
 * attack  – advance to target; assaults enemy units on the way / at the target.
 * hold    – stop and dig in where you stand (target = current hex).
 * retreat – disengage and fall back to target; breaks off battles at an org penalty.
 * posture – only changes posture (target ignored).
 */
export type OrderKind = 'move' | 'attack' | 'hold' | 'retreat' | 'posture';

/** How an order travels from HQ to the unit. */
export type OrderChannel = 'courier' | 'telephone' | 'radio';

export interface Order {
  id: OrderId;
  unitId: UnitId;
  faction: FactionId;
  kind: OrderKind;
  target: HexId;
  posture: Posture | null;
  /** H-hour: the unit waits until this tick before executing. -1 = execute on receipt. */
  executeAt: Tick;
  issuedAt: Tick;
  channel: OrderChannel;
  /** Optional operation name grouping several orders (shown in UI + intercepts). */
  opName: string | null;
}

export type UnitStatus =
  | 'idle'
  | 'moving'
  | 'attacking'
  | 'defending'
  | 'retreating'
  | 'entrenched'
  | 'awaiting' // holding a received order until its H-hour
  | 'routed'; // org collapsed, cannot act until recovered

export interface Unit {
  id: UnitId;
  faction: FactionId;
  type: UnitType;
  /** e.g. "12th Infantry Division", "II Army HQ". */
  name: string;
  hex: HexId;
  /** Remaining path (next hex first). Empty when stationary. */
  path: HexId[];
  /** Progress 0..1 of the current step towards path[0]. */
  moveProgress: number;
  strength: number;
  org: number;
  entrench: number;
  experience: number;
  /** Supply level at the unit's hex, refreshed by the supply system. */
  supply: number;
  /** Consecutive hours at (near) zero supply. */
  hoursOutOfSupply: number;
  posture: Posture;
  status: UnitStatus;
  /** Order currently being executed (already delivered). */
  order: Order | null;
  /** Delivered order waiting for its H-hour. */
  queuedOrder: Order | null;
  /** Battle id this unit participates in, or -1. */
  battle: number;
  /** For decoys: the type enemies believe this unit is. */
  disguise: UnitType | null;
  /** Tick the unit was created (for stats / UI). */
  createdAt: Tick;
  /** Id of the newest order delivered to this unit; older deliveries arriving later are stale. */
  lastOrderId: OrderId;
  /** True while the unit sits in a pocket cut off from its faction's main supply network. */
  encircled: boolean;
}

/** An order travelling by telephone or radio (couriers are physical entities, see `Courier`). */
export interface Dispatch {
  order: Order;
  /** Tick at which the order reaches the unit. */
  deliverAt: Tick;
}

export interface Courier {
  id: number;
  faction: FactionId;
  order: Order;
  targetUnit: UnitId;
  hex: HexId;
  path: HexId[];
  moveProgress: number;
  departedAt: Tick;
  /** 'rider' before the motor-couriers tech, 'motorcycle' after. */
  mode: 'rider' | 'motorcycle';
}

export interface Battle {
  id: number;
  /** The defended hex. */
  hex: HexId;
  attackers: UnitId[];
  defenders: UnitId[];
  startedAt: Tick;
  /** -1 (defender winning) .. +1 (attacker winning), for UI. */
  balance: number;
  /** Directions (bitmask over the 6 hex directions) attacks come from; >1 bit ⇒ flanking bonus. */
  attackDirs: number;
  /** Casualties in strength-points (sum of fractions) since start, for UI/stats. */
  attackerLosses: number;
  defenderLosses: number;
}

// ---------------------------------------------------------------------------------------------
// Intelligence
// ---------------------------------------------------------------------------------------------

/**
 * Detection level of an enemy unit.
 *  1 contact     – "something is there" (NATO frame with ?)
 *  2 identified  – type + designation known (decoys show their disguise)
 *  3 assessed    – strength/org estimate known, decoys unmasked
 */
export type IntelLevel = 1 | 2 | 3;

/** One faction's knowledge about one enemy unit (current or remembered). */
export interface IntelContact {
  unitId: UnitId;
  faction: FactionId;
  hex: HexId;
  level: IntelLevel;
  /** Believed type (level ≥ 2). Decoys report their disguise until assessed. */
  type: UnitType | null;
  name: string | null;
  /** Rounded to 0.1 (level 3 only). */
  strength: number | null;
  org: number | null;
  /** True once a decoy has been unmasked. */
  exposedDecoy: boolean;
  lastSeen: Tick;
  visibleNow: boolean;
  /** Moving direction when last seen (0..5) or -1. Rendered as a small arrow on ghosts. */
  heading: number;
}

/** An enemy order learned through courier capture or radio decryption. */
export interface InterceptedOrder {
  id: number;
  method: 'captured-courier' | 'decrypted-radio';
  tick: Tick;
  victim: FactionId;
  order: Order;
  unitName: string;
  unitType: UnitType | null;
  /** Where the unit was believed to be when the order was sent. */
  fromHex: HexId;
  /** Expires from the map after this tick. */
  expiresAt: Tick;
}

// ---------------------------------------------------------------------------------------------
// Factions, economy, research
// ---------------------------------------------------------------------------------------------

export type TechId =
  | 'field_telephone'
  | 'motor_couriers'
  | 'radio'
  | 'encryption'
  | 'codebreaking'
  | 'codebreaking2'
  | 'aerial_recon'
  | 'deception'
  | 'motorization'
  | 'armor2'
  | 'heavy_artillery'
  | 'combined_arms'
  | 'defense_in_depth'
  | 'railways'
  | 'field_hospitals';

export type TechBranch = 'signals' | 'intelligence' | 'warfare' | 'logistics';

export interface ProductionItem {
  id: number;
  unitType: UnitType;
  /** City where the unit appears (must still be owned + in supply when finished). */
  cityId: CityId;
  /** Industry already invested. */
  progress: number;
}

export type Controller = 'human' | 'ai';
export type AiDifficulty = 'recruit' | 'veteran' | 'general';

export interface FactionState {
  id: FactionId;
  name: string;
  adjective: string;
  /** 0xRRGGBB */
  color: number;
  team: number;
  controller: Controller;
  aiDifficulty: AiDifficulty;
  capitalCity: CityId;
  alive: boolean;
  capitulatedAt: Tick;
  /** Manpower pool (thousands). */
  manpower: number;
  /** Industry income per hour at last economy update (for UI). */
  industryRate: number;
  production: ProductionItem[];
  research: {
    current: TechId | null;
    progress: number;
    unlocked: TechId[];
  };
  /** Research points per hour (for UI). */
  researchRate: number;
  vp: number;
  stats: FactionStats;
}

export interface FactionStats {
  unitsProduced: number;
  unitsLost: number;
  enemyUnitsDestroyed: number;
  citiesCaptured: number;
  ordersIntercepted: number;
  couriersLost: number;
  peakHexes: number;
}

// ---------------------------------------------------------------------------------------------
// Events (routed to factions by `audience`)
// ---------------------------------------------------------------------------------------------

export type GameEventBody =
  | { kind: 'warDeclared'; theatre: string }
  | { kind: 'contact'; hex: HexId; enemyUnit: UnitId; spotter: UnitId }
  | {
      kind: 'battleStarted';
      battle: number;
      hex: HexId;
      attackerFaction: FactionId;
      defenderFaction: FactionId;
    }
  | { kind: 'battleEnded'; battle: number; hex: HexId; winner: 'attacker' | 'defender' }
  | { kind: 'unitRetreated'; unit: UnitId; from: HexId; to: HexId }
  | {
      kind: 'unitDestroyed';
      unit: UnitId;
      unitName: string;
      faction: FactionId;
      hex: HexId;
      cause: 'combat' | 'surrender' | 'attrition';
    }
  | { kind: 'cityCaptured'; city: CityId; from: FactionId; to: FactionId }
  | { kind: 'encircled'; faction: FactionId; units: UnitId[]; hex: HexId }
  | { kind: 'orderDelivered'; order: OrderId; unit: UnitId; delay: number }
  | { kind: 'orderExecuted'; order: OrderId; unit: UnitId }
  | { kind: 'courierLost'; order: OrderId; unit: UnitId; hex: HexId }
  | { kind: 'intercept'; intercept: InterceptedOrder }
  | { kind: 'unitProduced'; unit: UnitId; unitType: UnitType; city: CityId }
  | { kind: 'techResearched'; tech: TechId }
  | { kind: 'decoyExposed'; unit: UnitId; hex: HexId }
  | { kind: 'hqLost'; unit: UnitId; faction: FactionId }
  | { kind: 'capitulated'; faction: FactionId; by: FactionId }
  | { kind: 'gameOver'; winners: FactionId[]; reason: 'conquest' | 'victory-points' | 'surrender' }
  /**
   * The Meteorological Service's warning for a region along the addressee's front: `condition`
   * expected from about tick `at` with `chance` (0..100) per cent. Addressed to one faction.
   */
  | {
      kind: 'weatherForecast';
      region: number;
      hex: HexId;
      condition: ForecastCondition;
      at: Tick;
      chance: number;
    }
  /** A theatre-wide change of the ground (public): first frost, thaw, mud season, … */
  | { kind: 'weatherChange'; change: WeatherChange; hex: HexId };

export type GameEvent = GameEventBody & {
  tick: Tick;
  /** Factions that learn about this event. 'all' = public. */
  audience: FactionId[] | 'all';
};

// ---------------------------------------------------------------------------------------------
// Session configuration
// ---------------------------------------------------------------------------------------------

export type MapSize = 'small' | 'medium' | 'large';

export interface FactionSetup {
  controller: Controller;
  aiDifficulty?: AiDifficulty;
  team?: number;
  /** Optional overrides; otherwise generated. */
  name?: string;
  color?: number;
  /** Scenario asymmetry: techs this faction starts with, on top of `SessionConfig.startingTechs`. */
  techs?: TechId[];
  /** Scenario asymmetry: starting army size multiplier (0.5..2, default 1). */
  army?: number;
  /** Scenario asymmetry: additional starting units, deployed as a reserve near the capital. */
  extraUnits?: { type: UnitType; count: number }[];
}

export interface SessionConfig {
  seed: number;
  mapSize: MapSize;
  factions: FactionSetup[];
  /** Match ends after this many in-game days; highest VP wins. 0 = no limit. */
  dayLimit: number;
  /** Fraction of total VP a team must hold to win outright (0.5..1). */
  vpVictoryShare: number;
  /** Map-wide tech head start (e.g. start with radio) for quick games. */
  startingTechs?: TechId[];
  /** Season of the campaign (weather & ground); absent = chosen from the seed. */
  season?: Season;
  /** Scenario id (see scenarios.ts) when the match was set up from a preset; informational. */
  scenario?: string;
  /**
   * Real-world theatre (`TheatreId`, see theatres/index.ts) to fight on instead of a generated
   * map: the map and the nations' names and colours come from it, `mapSize` is informational,
   * and `factions.length` must equal the theatre's nation count.
   */
  theatre?: string;
}

// ---------------------------------------------------------------------------------------------
// Weather & seasons (see docs/Weather.md; rules helpers in core/weather.ts, system in sim/weather.ts)
// ---------------------------------------------------------------------------------------------

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';
export const SEASONS: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];

/**
 * A moving weather system: an elliptical precipitation field drifting with the wind.
 * Coordinates are in "hex units": world x / HEX_W, world y / HEX_W (one unit = one hex spacing).
 */
export interface WeatherCell {
  id: number;
  x: number;
  y: number;
  /** Unit vector of the long axis (fronts lie across their motion). */
  ux: number;
  uy: number;
  /** Semi-axes in hex units: along `u` and across it. */
  major: number;
  minor: number;
  /** Drift per hour (hex units). */
  vx: number;
  vy: number;
  /** Peak precipitation 0..1 (the core of a cell at full strength). */
  peak: number;
  /** Hours since the cell formed, and its lifetime (it builds up, holds, then decays). */
  age: number;
  life: number;
}

export type ForecastCondition = 'rain' | 'storm' | 'snow' | 'blizzard' | 'fog';
export type WeatherChange = 'frost' | 'thaw' | 'mud' | 'firm' | 'snow';

/**
 * 24 h outlook of the Meteorological Service — an ensemble of perturbed projections of the
 * current cells (plus systems that may still form), so its uncertainty grows honestly with the
 * lead time. Per region (map.regions order) and lead step: flat arrays indexed
 * `region * leads.length + step`.
 */
export interface WeatherForecast {
  issuedAt: Tick;
  /** Hours ahead of each outlook step. */
  leads: number[];
  /** Chance (0..100) of precipitation (rain or snow). */
  precip: Uint8Array;
  /** Chance (0..100) that it is heavy: storm or blizzard. */
  severe: Uint8Array;
  /** Chance (0..100) that precipitation falls as snow. */
  snow: Uint8Array;
  /** Per region: chance (0..100) of fog next morning (05–09). */
  fog: Uint8Array;
  /** Forecast sea-level air temperature range over the next 24 h (°C, whole degrees). */
  tempMin: number;
  tempMax: number;
}

/** Authoritative weather. Weather never depends on what the players do. */
export interface WeatherState {
  season: Season;
  /** false = static skies (tests, scenarios): nothing evolves, conditions stay as set. */
  live: boolean;
  /** Weather's own RNG stream: weather is a pure function of (seed, config, tick). */
  rngState: [number, number, number, number];
  nextCellId: number;
  /** Prevailing wind, hex units per hour (systems drift with it). */
  wind: { x: number; y: number };
  /** Slow random temperature anomaly (°C) — cold snaps and warm spells over days. */
  anomaly: number;
  /** Sea-level air temperature now (°C). */
  temperature: number;
  cells: WeatherCell[];
  /**
   * Strength (0..1) of the coming (or current) morning's fog, drawn at FOG_DRAW_HOUR the evening
   * before — so the evening and midnight bulletins can forecast it, the morning ones cannot.
   */
  fogStrength: number;
  /** Per hex: ground wetness 0..1, snow depth 0..1, frost 0..FROST_MAX (frozen at ≥ 1). */
  wetness: Float32Array;
  snow: Float32Array;
  frost: Float32Array;
  /** Per hex: W_* condition bits (core/weather.ts) — what every rule reads. */
  conditions: Uint8Array;
  forecast: WeatherForecast;
  /** Theatre-wide regimes behind the public `weatherChange` notices (with hysteresis). */
  regime: { frozen: boolean; mud: boolean; snow: boolean };
  /** Per faction: tick of the last MET warning and its key (region:condition) — rate limit. */
  metAt: number[];
  metKey: string[];
}

// ---------------------------------------------------------------------------------------------
// Full authoritative state (server/worker only — never sent raw to clients)
// ---------------------------------------------------------------------------------------------

export interface GameState {
  tick: Tick;
  config: SessionConfig;
  map: GameMap;
  factions: FactionState[];
  /** Current controller per hex. */
  owner: Uint8Array;
  units: Map<UnitId, Unit>;
  couriers: Map<number, Courier>;
  /** Telephone / radio orders in transit (delivered when `deliverAt` is reached). */
  dispatches: Dispatch[];
  battles: Map<number, Battle>;
  /** Per faction: supply level per hex (0..1) for hexes that faction controls, else 0. */
  supply: Float32Array[];
  /** Per faction: 1 if the hex is currently observed by that faction's team. */
  visibility: Uint8Array[];
  /** Per faction: last known owner per hex (updated where visible). */
  knownOwner: Uint8Array[];
  /** Per faction: intel on enemy units. */
  intel: Map<UnitId, IntelContact>[];
  /** Per faction: intercepted enemy orders currently known. */
  intercepts: InterceptedOrder[][];
  /** Events produced during the last tick. */
  events: GameEvent[];
  nextId: number;
  rngState: [number, number, number, number];
  /** Team currently holding ≥ vpVictoryShare of all VP, and since when (24 h ⇒ victory). */
  victoryClock: { team: number; since: Tick } | null;
  /** Set when the match is decided. */
  winners: FactionId[] | null;
  /** Sky and ground (public: everyone sees the weather). */
  weather: WeatherState;
}
